import { Check, CheckCircle2, XCircle } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import type { CoverageMatrix, WinTheme } from "@/stores/strategy-store"

interface WinThemeCoverageMatrixProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  coverage: CoverageMatrix
  themes: WinTheme[]
}

function formatWeight(row: CoverageMatrix['rows'][0]): string {
  if (row.weightPercentage != null) return `${row.weightPercentage}%`
  if (row.weightPoints != null) return `${row.weightPoints} pts`
  return ''
}

export function WinThemeCoverageMatrix({ open, onOpenChange, coverage, themes }: WinThemeCoverageMatrixProps) {
  const allCovered = coverage.coveredCriteria === coverage.totalCriteria && coverage.totalCriteria > 0

  // Calculate per-theme coverage weight
  const themeWeights = themes.map(theme => {
    const coveredRows = coverage.rows.filter(r => r.themeIds.includes(theme.id))
    const weight = coveredRows.reduce((s, r) => s + (r.weightPercentage ?? r.weightPoints ?? 0), 0)
    return { themeId: theme.id, weight }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto p-0">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle>Theme-to-Criteria Coverage</DialogTitle>
          <DialogDescription>Each evaluation criterion should be addressed by at least one win theme.</DialogDescription>
        </DialogHeader>

        <div className="overflow-x-auto">
          <TooltipProvider delayDuration={200}>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground w-56 align-bottom">Evaluation Criterion</th>
                  {themes.map(theme => (
                    <th key={theme.id} className="text-center px-2 py-3 text-xs font-medium text-muted-foreground align-bottom">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="leading-tight line-clamp-2 break-words min-w-[88px] max-w-[112px] mx-auto cursor-default">{theme.title}</div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">{theme.title}</TooltipContent>
                      </Tooltip>
                    </th>
                  ))}
                  <th className="text-center px-3 py-3 text-xs font-medium text-muted-foreground w-16 align-bottom">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {coverage.rows.map(row => {
                  const isCovered = row.themeIds.length > 0
                  return (
                    <tr key={row.criterionId} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="text-sm">{row.criterionTitle}</div>
                        {formatWeight(row) && (
                          <div className="text-meta">{formatWeight(row)} weight</div>
                        )}
                      </td>
                      {themes.map(theme => (
                        <td key={theme.id} className="text-center px-2 py-2.5">
                          {row.themeIds.includes(theme.id) ? (
                            <span className="inline-flex items-center justify-center h-[22px] w-[22px] rounded-md bg-[hsl(var(--status-success)/0.12)] text-[hsl(var(--status-success))]">
                              <Check className="h-3.5 w-3.5" strokeWidth={2.75} />
                            </span>
                          ) : null}
                        </td>
                      ))}
                      <td className="text-center px-3 py-2.5">
                        {isCovered ? (
                          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--status-success))] inline" />
                        ) : (
                          <XCircle className="h-4 w-4 text-[hsl(var(--destructive))] inline" />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="bg-muted/40 border-t border-border">
                  <td className="px-4 py-3 text-xs font-medium text-muted-foreground">Theme coverage</td>
                  {themes.map(theme => {
                    const tw = themeWeights.find(t => t.themeId === theme.id)
                    return (
                      <td key={theme.id} className="text-center px-2 py-3 text-xs font-medium">
                        {tw?.weight ? `${tw.weight}%` : '–'}
                      </td>
                    )
                  })}
                  <td className={`text-center px-3 py-3 text-xs font-medium ${coverage.coveragePercent > 0 ? 'text-[hsl(var(--status-success))]' : 'text-[hsl(var(--destructive))]'}`}>
                    {coverage.coveragePercent}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </TooltipProvider>
        </div>

        <div className={`px-5 py-3 border-t border-border flex items-center gap-2 ${allCovered ? 'bg-[hsl(var(--status-success)/0.05)]' : 'bg-[hsl(var(--destructive)/0.05)]'}`}>
          {allCovered ? (
            <>
              <CheckCircle2 className="h-4 w-4 text-[hsl(var(--status-success))]" />
              <span className="text-xs font-medium text-[hsl(var(--status-success))]">All evaluation criteria are addressed by at least one win theme.</span>
            </>
          ) : (
            <>
              <XCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
              <span className="text-xs font-medium text-[hsl(var(--destructive))]">
                {coverage.totalCriteria - coverage.coveredCriteria} evaluation criteria not yet addressed.
              </span>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
